import { buildScoutQuestionCitationLine } from "routes/Customer/Scout/hooks/scoutInternalEvents";
import { PopoverScoutTrigger } from "routes/Customer/Scout/PopoverScout/PopoverScoutTrigger";

import type { RequirementCitationDisplay } from "./RequirementCitations";

/** DOM id so the stepper tour can spotlight the button as one of its stops. */
export const SCOUT_CITATION_EXPLAINER_ANCHOR_ID = "scout-citation-explainer-trigger";

function formatCitations(
  citations: readonly RequirementCitationDisplay[],
  { withUrls }: { withUrls: boolean }
): string {
  return citations
    .map(({ citation, url }) => (withUrls ? `${citation} (${url})` : citation))
    .join("; ");
}

/**
 * "Ask Scout why this is required" trigger beside regulatory citations.
 * Renders when citations are present; PopoverScoutTrigger itself hides when
 * the user has no Scout access (Regulatory Advisor or Pro).
 */
export function ScoutCitationExplainer({
  submissionId,
  citations,
  formName,
}: {
  submissionId: string;
  citations: readonly RequirementCitationDisplay[];
  formName: string;
}) {
  if (citations.length === 0) {
    return null;
  }

  const citationList = formatCitations(citations, { withUrls: true });
  // The citation rides on its own sentinel line so the bubble can caption it
  // instead of burying it in the question.
  const question = [
    `Why is the ${formName} required for this submission, and what information does the regulation require?`,
    buildScoutQuestionCitationLine(formatCitations(citations, { withUrls: false })),
  ].join("\n");

  return (
    <PopoverScoutTrigger
      id={SCOUT_CITATION_EXPLAINER_ANCHOR_ID}
      question={question}
      context={`Submission id: ${submissionId}\nThe ${formName} is required by ${citationList}.`}
      tooltip="Ask Scout about this requirement"
      ariaLabel={`Ask Scout why the ${formName} is required`}
      sx={{ color: "text.secondary", ml: 1.5 }}
    />
  );
}
